import Vue from 'vue'
import Vuex from 'vuex'
import {
  dryerFeed,
  dryerOutput,
  evaporation,
  energyRequired,
  costPerYear,
  annualSaving,
  evaporatedWater,
  productionIncrease,
  liquid,
  solidsInLiquid,
  lostSolidsPerHour,
  lostSolids,
  wasteReduction,
  increasedDryerCapacity
} from './starch-math'

import {
  makePdf
} from './starch-pdf'

Vue.use(Vuex)

// root state object.
// each Vuex instance is just a single state tree.
const state = {
  //
  calculationType: 'waste', // 'waste' || 'energy' || 'speed'

  //
  pdfStatus: 'notready',

  // General
  operatingHours: 8000,
  energyCost: 15,
  sellingPrice: 1000,

  // Process
  cakeMoisture: 30,
  cakeMoisture2: 25,
  cakeMoistureAfterDryer: 2,
  washWater: 5,
  washWater2: 2,
  solidsContent: 5000,
  solidsContent2: 1000,
  solidsInFeed: 34,
  dryerEfficiency: 60,
  dewateringCapacity: 10,
  dryerCapacity: 10,
  increasedDryerCapacity: null,
  // Energy required to heat cake and evaporate 1 kg water
  heatEnergyRequired: 2500, // kJ/kg water

  mailTo: '',

  // Results
  currentResults: {
    dryerFeed: null,
    dryerOutput: null,
    evaporation: null,
    energyRequired: null,
    costPerYear: null,
    evaporatedWater: null,
    liquid: null,
    solidsInLiquid: null,
    lostSolidsPerHour: null,
    lostSolids: null,
    annualThroughput: null
  },

  upgradedResults: {
    dryerFeed: null,
    dryerOutput: null,
    evaporation: null,
    energyRequired: null,
    costPerYear: null,
    evaporatedWater: null,
    liquid: null,
    solidsInLiquid: null,
    lostSolidsPerHour: null,
    lostSolids: null,
    annualThroughput: null
  },

  annualSaving: null,
  productionIncrease: null,
  wasteReduction: null
}

// mutations are operations that actually mutates the state.
// each mutation handler gets the entire state tree as the
// first argument, followed by additional payload arguments.
// mutations must be synchronous and can be recorded by plugins
// for debugging purposes.
const mutations = {
  patch (state, payload) {
    for (var name in payload) {
      if (payload.hasOwnProperty(name)) {
        state[name] = payload[name]
      }
    }
    //
    // Current results
    //
    // - Drying cost (savings)
    state.currentResults.dryerFeed = dryerFeed(state)
    state.currentResults.dryerOutput = dryerOutput(state)
    state.currentResults.evaporation = evaporation({ ...state, ...state.currentResults })
    state.currentResults.energyRequired = energyRequired({ ...state, ...state.currentResults })
    state.currentResults.costPerYear = costPerYear({ ...state, ...state.currentResults })

    // - Dryer throughput (speed)
    state.currentResults.dryerCapacity = state.dryerCapacity
    state.currentResults.evaporatedWater = evaporatedWater({ ...state, ...state.currentResults })
    state.currentResults.annualThroughput = state.dryerCapacity * state.operatingHours

    // - Losses to waste (waste)
    state.currentResults.liquid = liquid({ ...state, ...state.currentResults })
    state.currentResults.solidsInLiquid = solidsInLiquid({ ...state, ...state.currentResults })
    state.currentResults.lostSolidsPerHour = lostSolidsPerHour({ ...state, ...state.currentResults })
    state.currentResults.lostSolids = lostSolids({ ...state, ...state.currentResults })

    //
    // Updated results
    //

    // - Drying const
    state.upgradedResults.dryerFeed = dryerFeed({ ...state, cakeMoisture: state.cakeMoisture2 })
    state.upgradedResults.dryerOutput = dryerOutput(state)
    state.upgradedResults.evaporation = evaporation({ ...state, ...state.upgradedResults })
    state.upgradedResults.energyRequired = energyRequired({ ...state, ...state.upgradedResults })
    state.upgradedResults.costPerYear = costPerYear({ ...state, ...state.upgradedResults })

    // - Dryer throughput
    state.upgradedResults.evaporatedWater = evaporatedWater({ ...state, ...state.upgradedResults, cakeMoisture: state.cakeMoisture2 })

    // - Losses to waste
    state.upgradedResults.liquid = liquid({ ...state, ...state.upgradedResults, cakeMoisture: state.cakeMoisture2, washWater: state.washWater2 })
    state.upgradedResults.solidsInLiquid = solidsInLiquid({ ...state, ...state.upgradedResults, solidsContent: state.solidsContent2 })
    state.upgradedResults.lostSolidsPerHour = lostSolidsPerHour({ ...state, ...state.upgradedResults })
    state.upgradedResults.lostSolids = lostSolids({ ...state, ...state.upgradedResults })

    //
    // Comparing
    //
    state.annualSaving = annualSaving(state.currentResults, state.upgradedResults)
    state.productionIncrease = productionIncrease(state.currentResults, state.upgradedResults)
    state.wasteReduction = wasteReduction(state.currentResults, state.upgradedResults)
    state.increasedDryerCapacity = increasedDryerCapacity({ ...state })
    state.upgradedResults.annualThroughput = state.increasedDryerCapacity * state.operatingHours
  },

  createPdf (state) {
    makePdf(state)
  }

}

// actions are functions that cause side effects and can involve
// asynchronous operations.
const actions = {
  patch ({ commit, state }, payload) {
    if (payload) {
      commit('patch', payload)
    }
  },

  numberPatch ({ commit, state }, payload) {
    if (payload) {
      for (var name in payload) {
        if (isNaN(payload[name])) {
          return
        } else {
          payload[name] = Number(payload[name])
        }
      }
      commit('patch', payload)
    }
  },

  preparePdf ({commit, state}, payload) {
    commit('preparePdf')
  },

  createPdf ({commit, state}, payload) {
    commit('createPdf')
  }
}

// getters are functions
const getters = {
  // Calculation
  calculationType: state => state.calculationType,

  pdfStatus: state => state.pdfStatus,

  // General
  operatingHours: state => state.operatingHours,
  energyCost: state => state.energyCost,
  sellingPrice: state => state.sellingPrice,

  // Process
  // dewateringCapacity: state => state.dewateringCapacity,
  dewateringCapacity: state => state.dewateringCapacity,
  cakeMoisture: state => state.cakeMoisture,
  cakeMoisture2: state => state.cakeMoisture2,
  cakeMoistureAfterDryer: state => state.cakeMoistureAfterDryer,
  washWater: state => state.washWater,
  washWater2: state => state.washWater2,
  solidsContent: state => state.solidsContent,
  solidsContent2: state => state.solidsContent2,
  dryerEfficiency: state => state.dryerEfficiency,
  solidsInFeed: state => state.solidsInFeed,
  dryerCapacity: state => state.dryerCapacity,
  increasedDryerCapacity: state => state.increasedDryerCapacity,
  heatEnergyRequired: state => state.heatEnergyRequired,

  mailTo: state => state.mailTo,

  // Results
  currentResults: state => state.currentResults,
  upgradedResults: state => state.upgradedResults,
  annualSaving: state => state.annualSaving,
  productionIncrease: state => state.productionIncrease,
  wasteReduction: state => state.wasteReduction
}

// A Vuex instance is created by combining the state, mutations, actions,
// and getters.
export default new Vuex.Store({
  state,
  getters,
  actions,
  mutations
})



// WEBPACK FOOTER //
// ./src/store.js